import { Avatar, useDisclosure } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { useEffect, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import { T_KEY } from "@/src/constants/translationsKey";

import { dialogMessage } from "../../../../constants/dialogMessage";
import { TEXT } from "../../../../constants/user-management/consts";
import useAuth from "../../../../hooks/user-management/useAuth";
import useUserAuthStore, {
  checkLoggedIn,
} from "../../../../store/client/user-management/auth";
import { BUTTONS } from "../../../../utils/common/getErrorMessage";
import { LOCALES_SHORT } from "../../../../utils/types";
import Logo from "../../../customer-service/Logo/Logo";
import LogoContainer from "../../../customer-service/Logo/LogoContainer";
import CustomAlertDialog from "../../auth/CustomAlertDialog/CustomAlertDialog";
import CustomBlueButton from "../../auth/CustomBlueButton/CustomBlueButton";
import CustomUserMenu from "../../auth/CustomMenu/CustomMenu";
import Loader from "../../Loader/Loader";

import styles from "./LayoutHeader.module.scss";

const subscribeNoop = () => () => {};

function UserUI() {
  const { t } = useTranslation(T_KEY);
  const router = useRouter();
  const { currentUser } = useAuth(true);
  // localStorage 플래그는 서버에서 읽을 수 없으므로 서버·hydration 시점엔 false (hydration mismatch 방지)
  const isLoggedIn = useSyncExternalStore(
    subscribeNoop,
    checkLoggedIn,
    () => false
  );
  const { locale } = useRouter();
  const transformLocale =
    locale === LOCALES_SHORT.KO ? LOCALES_SHORT.KO : LOCALES_SHORT.EN;

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isDeleted, setIsDeleted } = useUserAuthStore(
    useShallow(state => ({
      isDeleted: state.isDeleted,
      setIsDeleted: state.setIsDeleted,
    }))
  );

  // 로그인 버튼 클릭 시 로그인 페이지로 이동
  const handleLoginClick = () => {
    router.push("/login");
  };

  const closeErrorDialog = () => {
    onClose();
    setIsDeleted(false);
  };

  useEffect(() => {
    if (isDeleted) {
      onOpen();
    }
  }, [isDeleted, onOpen]);

  return (
    <div className={styles.userUI}>
      {currentUser || isLoggedIn ? (
        // 로그인된 유저의 아바타 이미지와 메뉴
        <CustomUserMenu icon={<Avatar size="sm" />} />
      ) : (
        <CustomBlueButton onClick={handleLoginClick}>
          {t(TEXT.login)}
        </CustomBlueButton>
      )}

      {/* CustomAlertDialog for displaying errors */}
      <CustomAlertDialog
        isOpen={isOpen}
        onClose={closeErrorDialog}
        title={dialogMessage.withdrawalSuccess.title}
        body={dialogMessage.withdrawalSuccess.message}
        alertDialogFooterProps={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
        footerButtons={BUTTONS(transformLocale).DELETE_ACCOUNT_SUCCESS}
      />
    </div>
  );
}

interface LayoutHeaderProps {
  showUserMenuOrLoginButton?: boolean;
  centered?: boolean;
}

function LayoutHeader({
  showUserMenuOrLoginButton = false,
  centered = false,
}: LayoutHeaderProps) {
  const { isLoading } = useAuth(false);

  return (
    <LogoContainer centered={centered}>
      <Logo alt="header-logo" />
      {/* 로그인 페이지에서는 보여지지 않는 영역 */}
      {showUserMenuOrLoginButton ? (
        isLoading ? (
          <Loader overlay />
        ) : (
          <UserUI />
        )
      ) : (
        <div className={styles.altUserUI} />
      )}
    </LogoContainer>
  );
}

export default LayoutHeader;
